import { useSubscription } from "@/lib/SubscriptionContext";
import { Lock, Sparkles, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import logger from "@/lib/logger";

export function LockedOverlay({ feature, children, compact = false }) {
  const { isFeatureLocked, trial } = useSubscription();
  const navigate = useNavigate();
  if (!FEATURE_FLAGS.PAYWALL_ENABLED) return children;
  const locked = isFeatureLocked(feature);

  if (!locked) return children;

  const canTrial = !trial?.used && !trial?.active;

  return (
    <div className="relative" data-testid={`locked-${feature}`}>
      <div className="pointer-events-none select-none" style={{ filter: "blur(3px)", opacity: 0.4 }}>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className={`flex flex-col items-center gap-3 ${compact ? "p-4" : "p-8"} rounded-2xl backdrop-blur-md max-w-xs`}
          style={{ background: "rgba(0,0,0,0.65)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <Lock className={compact ? "h-5 w-5" : "h-8 w-8"} style={{ color: "#F59E0B" }} />
          <span className={`${compact ? "text-xs" : "text-base"} font-bold text-center`} style={{ color: "#FFFFFF" }}>
            Unlock with Pro
          </span>
          <p className="text-xs text-center" style={{ color: "#A1A1AA" }}>
            Level up your workflow
          </p>
          <div className="flex flex-col gap-2 w-full">
            {canTrial && (
              <button onClick={() => navigate("/upgrade")}
                className={`flex items-center justify-center gap-1.5 w-full ${compact ? "px-3 py-2 text-xs" : "px-5 py-2.5 text-sm"} rounded-xl font-semibold transition-all hover:scale-[1.02]`}
                style={{ background: "#0D9488", color: "#FFFFFF" }}
                data-testid={`trial-btn-${feature}`}>
                <Sparkles className={compact ? "h-3 w-3" : "h-4 w-4"} /> Try Pro Free
              </button>
            )}
            <button onClick={() => navigate("/upgrade")}
              className={`flex items-center justify-center gap-1.5 w-full ${compact ? "px-3 py-2 text-xs" : "px-5 py-2.5 text-sm"} rounded-xl font-semibold transition-all hover:scale-[1.02]`}
              style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#FFFFFF" }}
              data-testid={`upgrade-btn-${feature}`}>
              <Crown className={compact ? "h-3 w-3" : "h-4 w-4"} /> Upgrade
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Crown({ className, style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" />
      <path d="M5 21h14" />
    </svg>
  );
}

export function ProBadge() {
  const { isPro, isTrial, trial } = useSubscription();
  if (!FEATURE_FLAGS.SHOW_UPGRADE_PROMPTS && !isPro) return null;
  if (!isPro) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: isTrial ? "#0D9488" : "linear-gradient(135deg, #F59E0B, #D97706)", color: "#FFFFFF" }}
      data-testid="pro-badge">
      {isTrial ? (
        <><Clock className="h-2.5 w-2.5" /> TRIAL {trial?.days_left}d</>
      ) : (
        <><Sparkles className="h-2.5 w-2.5" /> PRO</>
      )}
    </span>
  );
}

export function UpgradePrompt({ trigger, onDismiss }) {
  const navigate = useNavigate();
  const { trial, startTrial } = useSubscription();
  if (!FEATURE_FLAGS.SHOW_UPGRADE_PROMPTS) { onDismiss(); return null; }
  const canTrial = !trial?.used && !trial?.active;

  const messages = {
    streak_milestone: { title: "You're building momentum!", desc: "Unlock unlimited streaks and ADHD mode" },
    level_up: { title: "Level up your workflow!", desc: "Unlock full Game Mode with Pro" },
    locked_feature: { title: "Unlock your full system", desc: "Experience every feature with Pro" },
    task_complete: { title: "Great progress!", desc: "Boost your productivity with Pro tools" },
  };

  const msg = messages[trigger] || messages.locked_feature;

  const handleTrial = async () => {
    try {
      await startTrial();
      onDismiss();
    } catch (e) { logger.error("startTrial:", e); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onDismiss} data-testid="upgrade-prompt">
      <div className="max-w-sm w-full mx-4 p-6 rounded-2xl space-y-4"
        style={{ background: "#1A1A2E", border: "1px solid #30363D" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #F59E0B20, #D9770620)", border: "1px solid #F59E0B30" }}>
            <Sparkles className="h-7 w-7" style={{ color: "#F59E0B" }} />
          </div>
          <h3 className="text-lg font-bold" style={{ color: "#F0F6FC" }}>{msg.title}</h3>
          <p className="text-sm" style={{ color: "#8B949E" }}>{msg.desc}</p>
        </div>
        <div className="space-y-2">
          {canTrial && (
            <button onClick={handleTrial}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.01]"
              style={{ background: "#0D9488", color: "#FFFFFF" }}
              data-testid="upgrade-prompt-trial">
              <Sparkles className="h-4 w-4" /> Try Pro Free for 7 Days
            </button>
          )}
          <button onClick={() => { onDismiss(); navigate("/upgrade"); }}
            className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.01]"
            style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#FFFFFF" }}
            data-testid="upgrade-prompt-go">
            Upgrade to Pro
          </button>
          <button onClick={onDismiss}
            className="w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{ background: "#21262D", color: "#8B949E", border: "1px solid #30363D" }}
            data-testid="upgrade-prompt-dismiss">
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export function LockedThemeBadge({ themeId }) {
  const { isThemeUnlocked } = useSubscription();
  if (isThemeUnlocked(themeId)) return null;
  return (
    <div className="absolute top-1 right-1 z-10">
      <Lock className="h-3 w-3" style={{ color: "#F59E0B" }} />
    </div>
  );
}

export function TrialBanner() {
  const { isTrial, trial } = useSubscription();
  const navigate = useNavigate();
  if (!isTrial) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
      style={{ background: "#0D948810", border: "1px solid #0D948820" }}
      data-testid="trial-inline-banner">
      <Clock className="h-3.5 w-3.5 shrink-0" style={{ color: "#0D9488" }} />
      <span style={{ color: "#0D9488" }}>
        <strong>Trial:</strong> {trial?.days_left}d left
      </span>
      <button onClick={() => navigate("/upgrade")} className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-lg"
        style={{ background: "#F59E0B", color: "#FFF" }}>
        Subscribe
      </button>
    </div>
  );
}
